<template>
  <div class="w-full text-zinc-900 dark:text-zinc-50">
    <section
      class="overflow-hidden rounded-[28px] border border-zinc-200/80 bg-white/90 shadow-[0_24px_70px_rgba(15,23,42,0.10)] backdrop-blur-xl dark:border-zinc-800/80 dark:bg-[#09090b]/95 dark:shadow-[0_24px_70px_rgba(0,0,0,0.34)]"
    >
      <header
        class="flex min-h-[76px] flex-col gap-3 border-b border-zinc-200/80 bg-white/80 px-4 py-3 dark:border-zinc-800 dark:bg-[#0b0b0e]/90 sm:flex-row sm:items-center sm:justify-between lg:px-5"
      >
        <div class="flex min-w-0 items-center gap-3">
          <button
            type="button"
            @click="goBackToProjects"
            class="grid h-10 w-10 shrink-0 place-items-center rounded-2xl border border-zinc-200 bg-zinc-50 text-zinc-500 transition hover:-translate-x-0.5 hover:border-zinc-300 hover:text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-zinc-700 dark:hover:text-white"
            title="Back to texture projects"
          >
            <span class="material-symbols-rounded text-[18px]">arrow_back</span>
          </button>

          <div class="min-w-0">
            <div class="flex min-w-0 items-center gap-2">
              <h1 class="truncate text-base font-semibold tracking-tight text-zinc-950 dark:text-white sm:text-lg">
                {{ project?.name || 'Texture project' }}
              </h1>
              <span
                class="inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.14em]"
                :class="saveStatusClass"
                :title="lastSavedAt ? `Last save: ${lastSavedAt}` : ''"
              >
                <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
                {{ saveStatusLabel }}
              </span>
            </div>
            <div class="mt-0.5 flex min-w-0 items-center gap-2 text-[10px] text-zinc-500 dark:text-zinc-400">
              <span class="truncate">{{ curStyleConfig.name }}</span>
              <span class="text-zinc-300 dark:text-zinc-700">/</span>
              <span class="font-mono">{{ seed }}</span>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2 sm:justify-end">
          <button
            type="button"
            @click="randomSeed"
            :disabled="!projectReady"
            class="inline-flex h-9 items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 text-[10px] font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            <span class="material-symbols-rounded text-[16px]" :class="isSpinning ? 'animate-spin' : ''">casino</span>
            Seed
          </button>

          <button
            type="button"
            @click="randomizeCurrent"
            :disabled="!projectReady || remixing"
            class="inline-flex h-9 items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 text-[10px] font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            <span class="material-symbols-rounded text-[16px]">shuffle</span>
            {{ remixing ? 'Remixing…' : 'Remix' }}
          </button>

          <div class="hidden h-7 w-px bg-zinc-200 dark:bg-zinc-800 sm:block"></div>

          <button
            type="button"
            @click="saveAndOpenFolderModal"
            :disabled="!projectReady || isSavingToFolder"
            class="inline-flex h-9 items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 text-[10px] font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            <span class="material-symbols-rounded text-[16px]">folder_open</span>
            Save to..
          </button>

          <button
            type="button"
            @click="saveProjectState"
            :disabled="!projectReady || savingProject"
            class="inline-flex h-9 items-center gap-2 rounded-xl bg-emerald-500 px-3.5 text-[10px] font-bold text-zinc-950 shadow-[0_8px_24px_rgba(16,185,129,0.18)] transition hover:bg-emerald-400 disabled:opacity-40"
          >
            <span class="material-symbols-rounded text-[16px]">save</span>
            {{ savingProject ? 'Saving…' : 'Save changes' }}
          </button>

          <button
            type="button"
            @click="doExport"
            :disabled="exporting || !projectReady"
            class="inline-flex h-9 items-center gap-2 rounded-xl bg-zinc-950 px-4 text-[10px] font-bold text-white transition hover:bg-zinc-800 disabled:opacity-40 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100"
          >
            <span class="material-symbols-rounded text-[16px]">download</span>
            {{ exporting ? 'Exporting…' : `Export ${exportSize === 8192 ? '8K' : '4K'}` }}
          </button>
        </div>
      </header>

      <div
        class="grid min-h-[700px] xl:h-[calc(100vh-240px)] xl:min-h-[700px] xl:max-h-[900px] xl:grid-cols-[220px_minmax(0,1fr)_320px]"
      >
        <aside
          class="flex min-h-0 flex-col border-b border-zinc-200 bg-zinc-50/70 dark:border-zinc-800 dark:bg-[#0b0b0e] xl:border-b-0 xl:border-r"
        >
          <div class="flex h-[58px] shrink-0 items-center justify-between border-b border-zinc-200 px-4 dark:border-zinc-800">
            <div>
              <p class="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                Models
              </p>
              <p class="mt-0.5 text-[10px] text-zinc-400 dark:text-zinc-500">
                {{ STYLES.length }} texture engines
              </p>
            </div>
            <span class="material-symbols-rounded text-[17px] text-zinc-400">view_list</span>
          </div>

          <div class="grid max-h-[320px] grid-cols-2 gap-2 overflow-y-auto p-3 custom-scroll xl:max-h-none xl:flex-1 xl:grid-cols-1">
            <button
              v-for="s in STYLES"
              :key="s.id"
              type="button"
              @click="switchStyle(s.id)"
              class="group flex min-w-0 items-center gap-2.5 rounded-2xl border px-2.5 py-2.5 text-left transition-all"
              :class="currentStyle === s.id
                ? 'border-sky-500/40 bg-sky-500/10 text-sky-700 shadow-sm dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-300'
                : 'border-transparent bg-transparent text-zinc-600 hover:border-zinc-200 hover:bg-white dark:text-zinc-400 dark:hover:border-zinc-800 dark:hover:bg-zinc-900'"
            >
              <span
                class="grid h-8 w-8 shrink-0 place-items-center rounded-xl border text-[14px]"
                :class="currentStyle === s.id
                  ? 'border-sky-500/20 bg-sky-500/10'
                  : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'"
              >
                {{ presetIcon(s) }}
              </span>

              <span class="min-w-0">
                <span class="block truncate text-[10px] font-semibold text-zinc-900 dark:text-zinc-100">
                  {{ s.name }}
                </span>
                <span class="mt-0.5 block truncate text-[8px] uppercase tracking-[0.12em] text-zinc-400 dark:text-zinc-500">
                  {{ s.category || 'Texture' }}
                </span>
              </span>
            </button>
          </div>

          <div class="mt-auto hidden shrink-0 border-t border-zinc-200 p-3 dark:border-zinc-800 xl:block">
            <div class="rounded-2xl border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900/70">
              <div class="flex items-center justify-between gap-2">
                <span class="text-[9px] font-bold uppercase tracking-[0.14em] text-zinc-400">Active</span>
                <span class="h-2 w-2 rounded-full bg-emerald-500"></span>
              </div>
              <p class="mt-2 truncate text-[11px] font-semibold text-zinc-800 dark:text-zinc-200">
                {{ curStyleConfig.name }}
              </p>
              <p class="mt-1 truncate text-[9px] text-zinc-400">
                {{ curStyleConfig.engine || curStyleConfig.category || 'Texture Engine' }}
              </p>
            </div>
          </div>
        </aside>

        <main class="relative flex min-h-0 flex-col overflow-hidden bg-zinc-100/60 dark:bg-[#050507]">
          <div
            class="flex h-[58px] shrink-0 items-center justify-between gap-3 border-b border-zinc-200 bg-white/65 px-4 dark:border-zinc-800 dark:bg-[#09090b]/80"
          >
            <div class="flex min-w-0 items-center gap-3">
              <span class="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-300">
                {{ presetIcon(curStyleConfig) }}
              </span>
              <div class="min-w-0">
                <p class="truncate text-[11px] font-semibold text-zinc-900 dark:text-zinc-100">
                  {{ curStyleConfig.name }}
                </p>
                <p class="truncate text-[9px] uppercase tracking-[0.12em] text-zinc-400 dark:text-zinc-500">
                  Live preview
                </p>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <span
                class="hidden items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[8px] font-semibold uppercase tracking-[0.12em] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 sm:inline-flex"
              >
                <span
                  class="h-1.5 w-1.5 rounded-full"
                  :class="rendering ? 'animate-pulse bg-amber-400' : 'bg-emerald-500'"
                ></span>
                {{ rendering ? 'Rendering' : 'Ready' }}
              </span>
              <span
                class="rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[8px] font-semibold uppercase tracking-[0.12em] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400"
              >
                {{ PREVIEW_SIZE }}px
              </span>
            </div>
          </div>

          <div class="relative flex min-h-[470px] flex-1 items-center justify-center overflow-hidden p-4 sm:p-6 xl:min-h-0">
            <div class="pointer-events-none absolute inset-0 opacity-40 dark:opacity-20">
              <div class="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(14,165,233,0.10),transparent_48%)]"></div>
            </div>

            <div class="relative aspect-square h-auto max-h-full w-full max-w-[720px]">
              <div
                class="relative h-full w-full overflow-hidden rounded-[30px] border border-zinc-300 bg-zinc-900 p-2 shadow-[0_28px_90px_rgba(15,23,42,0.18)] dark:border-zinc-800 dark:bg-black dark:shadow-[0_28px_90px_rgba(0,0,0,0.55)]"
              >
                <div class="relative h-full w-full overflow-hidden rounded-[23px] bg-black">
                  <img
                    v-if="previewUrl"
                    :src="previewUrl"
                    :alt="`${curStyleConfig.name} texture preview`"
                    class="block h-full w-full object-cover"
                    draggable="false"
                  />

                  <div v-else class="grid h-full w-full place-items-center bg-black">
                    <div class="text-center">
                      <div
                        class="mx-auto h-8 w-8 rounded-full border-2 border-white/10 border-t-sky-400"
                        :class="(loadingPresets || loadingProject || rendering) ? 'animate-spin' : ''"
                      ></div>
                      <p class="mt-3 text-[9px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                        {{ loadingProject ? 'Loading project' : rendering ? 'Rendering preview' : 'Preview unavailable' }}
                      </p>
                    </div>
                  </div>

                  <div
                    v-if="rendering && previewUrl"
                    class="pointer-events-none absolute inset-0 grid place-items-center bg-black/10 backdrop-blur-[1px]"
                  >
                    <div class="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/75 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white">
                      <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-400"></span>
                      Updating
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="errorMessage"
              class="absolute bottom-4 left-1/2 z-20 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 rounded-2xl border border-red-200 bg-red-50/95 px-4 py-3 text-[11px] font-medium text-red-700 shadow-xl backdrop-blur dark:border-red-900/50 dark:bg-red-950/90 dark:text-red-200"
            >
              {{ errorMessage }}
            </div>
          </div>

          <div
            class="flex min-h-[58px] shrink-0 flex-wrap items-center justify-between gap-3 border-t border-zinc-200 bg-white/65 px-4 py-2 dark:border-zinc-800 dark:bg-[#09090b]/80"
          >
            <div class="flex items-center gap-2 text-[9px] text-zinc-500 dark:text-zinc-400">
              <span class="material-symbols-rounded text-[15px] text-sky-500">casino</span>
              <span class="uppercase tracking-[0.12em]">Seed</span>
              <span class="rounded-lg bg-zinc-100 px-2 py-1 font-mono text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
                {{ seed }}
              </span>
            </div>

            <div class="inline-flex rounded-xl border border-zinc-200 bg-zinc-100 p-1 dark:border-zinc-800 dark:bg-zinc-900">
              <button
                v-for="s in EXPORT_SIZES"
                :key="s.value"
                type="button"
                @click="exportSize = s.value"
                class="h-7 rounded-lg px-3 text-[9px] font-semibold transition"
                :class="exportSize === s.value
                  ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                  : 'text-zinc-500 dark:text-zinc-400'"
              >
                {{ s.short }}
              </button>
            </div>
          </div>
        </main>

        <aside
          class="flex min-h-0 flex-col border-t border-zinc-200 bg-white dark:border-zinc-800 dark:bg-[#0b0b0e] xl:border-l xl:border-t-0"
        >
          <div class="shrink-0 border-b border-zinc-200 p-3 dark:border-zinc-800">
            <div class="grid grid-cols-3 rounded-2xl bg-zinc-100 p-1 dark:bg-zinc-900">
              <button
                type="button"
                @click="inspectorTab = 'controls'"
                class="h-8 rounded-xl text-[9px] font-bold uppercase tracking-[0.10em] transition"
                :class="inspectorTab === 'controls'
                  ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                  : 'text-zinc-500 dark:text-zinc-400'"
              >
                Controls
              </button>
              <button
                type="button"
                @click="inspectorTab = 'colors'"
                class="h-8 rounded-xl text-[9px] font-bold uppercase tracking-[0.10em] transition"
                :class="inspectorTab === 'colors'
                  ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                  : 'text-zinc-500 dark:text-zinc-400'"
              >
                Colors
              </button>
              <button
                type="button"
                @click="inspectorTab = 'output'"
                class="h-8 rounded-xl text-[9px] font-bold uppercase tracking-[0.10em] transition"
                :class="inspectorTab === 'output'
                  ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                  : 'text-zinc-500 dark:text-zinc-400'"
              >
                Output
              </button>
            </div>
          </div>

          <div class="min-h-0 flex-1 overflow-y-auto p-4 custom-scroll">
            <div v-if="inspectorTab === 'controls'">
              <div class="mb-4">
                <p class="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                  Parameters
                </p>
                <p class="mt-1 text-[11px] font-semibold text-zinc-800 dark:text-zinc-200">
                  {{ curStyleConfig.name }}
                </p>
                <p class="mt-0.5 text-[9px] text-zinc-400">
                  {{ Object.keys(curStyleConfig.params || {}).length }} adjustable controls
                </p>
              </div>

              <div
                v-if="!Object.keys(curStyleConfig.params || {}).length"
                class="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 p-5 text-center text-[10px] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-400"
              >
                This model has no adjustable parameters.
              </div>

              <div v-else class="space-y-2.5">
                <div
                  v-for="(cfg, key) in curStyleConfig.params"
                  :key="key"
                  class="rounded-2xl border border-zinc-200 bg-zinc-50/70 p-3 dark:border-zinc-800 dark:bg-zinc-900/55"
                >
                  <div class="mb-2.5 flex items-center justify-between gap-3">
                    <span class="truncate text-[9px] font-bold uppercase tracking-[0.10em] text-zinc-500 dark:text-zinc-400">
                      {{ cfg.label }}
                    </span>
                    <span
                      v-if="cfg.type === 'number'"
                      class="rounded-lg bg-white px-2 py-1 text-[8px] font-mono text-zinc-600 shadow-sm dark:bg-zinc-950 dark:text-zinc-300"
                    >
                      {{ displayParamValue(key, cfg) }}
                    </span>
                  </div>

                  <select
                    v-if="cfg.type === 'select'"
                    :value="getParam(key, cfg)"
                    @change="e => setParam(key, e.target.value)"
                    class="h-9 w-full rounded-xl border border-zinc-200 bg-white px-3 text-[10px] font-medium text-zinc-700 outline-none focus:border-sky-400 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200"
                  >
                    <option v-for="opt in cfg.options" :key="opt" :value="opt">{{ opt }}</option>
                  </select>

                  <button
                    v-else-if="cfg.type === 'boolean'"
                    type="button"
                    @click="setParam(key, !getParam(key, cfg))"
                    class="flex h-9 w-full items-center justify-between rounded-xl border px-3 text-[10px] font-medium transition"
                    :class="getParam(key, cfg)
                      ? 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300'
                      : 'border-zinc-200 bg-white text-zinc-500 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-400'"
                  >
                    <span>{{ getParam(key, cfg) ? 'Enabled' : 'Disabled' }}</span>
                    <span class="material-symbols-rounded text-[17px]">
                      {{ getParam(key, cfg) ? 'toggle_on' : 'toggle_off' }}
                    </span>
                  </button>

                  <input
                    v-else
                    type="range"
                    :min="cfg.min"
                    :max="cfg.max"
                    :step="cfg.step"
                    :value="getParam(key, cfg)"
                    @input="e => setParam(key, cfg.step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))"
                    class="mt-1 w-full cursor-grab"
                  />
                </div>
              </div>
            </div>

            <div v-else-if="inspectorTab === 'colors'">
              <div class="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p class="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                    Color System
                  </p>
                  <p class="mt-1 text-[10px] text-zinc-400">
                    Three-channel texture palette
                  </p>
                </div>

                <button
                  type="button"
                  @click="applyRandomPalette"
                  :disabled="!projectReady"
                  class="inline-flex h-8 items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-2.5 text-[9px] font-semibold text-zinc-600 transition hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  <span class="material-symbols-rounded text-[14px]">shuffle</span>
                  Shuffle
                </button>
              </div>

              <div class="space-y-3">
                <div
                  v-for="c in colorFields"
                  :key="c.label"
                  class="rounded-2xl border border-zinc-200 bg-zinc-50/70 p-3 dark:border-zinc-800 dark:bg-zinc-900/55"
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-zinc-200 shadow-inner dark:border-zinc-700"
                    >
                      <div class="absolute inset-0" :style="{ backgroundColor: c.val }"></div>
                      <input
                        type="color"
                        :value="c.val"
                        @input="e => c.set(e.target.value)"
                        class="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      />
                    </div>

                    <div class="min-w-0 flex-1">
                      <p class="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                        {{ c.label === 'A' ? 'Foreground' : c.label === 'B' ? 'Secondary' : 'Background' }}
                      </p>
                      <p class="mt-1 truncate font-mono text-[11px] text-zinc-700 dark:text-zinc-200">
                        {{ c.val.toUpperCase() }}
                      </p>
                    </div>

                    <span class="material-symbols-rounded text-[16px] text-zinc-400">palette</span>
                  </div>
                </div>
              </div>

              <div class="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                <p class="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                  Palette Preview
                </p>
                <div class="mt-3 flex h-9 overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700">
                  <div class="flex-1" :style="{ backgroundColor: color1 }"></div>
                  <div class="flex-1" :style="{ backgroundColor: color2 }"></div>
                  <div class="flex-1" :style="{ backgroundColor: bg }"></div>
                </div>
              </div>
            </div>

            <div v-else>
              <div class="mb-4">
                <p class="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                  Output
                </p>
                <p class="mt-1 text-[10px] text-zinc-400">
                  Export and library options
                </p>
              </div>

              <div class="rounded-2xl border border-zinc-200 bg-zinc-50/70 p-3 dark:border-zinc-800 dark:bg-zinc-900/55">
                <p class="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                  Resolution
                </p>

                <div class="mt-3 grid grid-cols-2 gap-2">
                  <button
                    v-for="s in EXPORT_SIZES"
                    :key="s.value"
                    type="button"
                    @click="exportSize = s.value"
                    class="rounded-xl border px-3 py-3 text-left transition"
                    :class="exportSize === s.value
                      ? 'border-sky-500/40 bg-sky-500/10'
                      : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-950'"
                  >
                    <span class="block text-[11px] font-semibold text-zinc-900 dark:text-zinc-100">
                      {{ s.short }}
                    </span>
                    <span class="mt-1 block text-[8px] text-zinc-400">
                      {{ s.value }} × {{ s.value }}
                    </span>
                  </button>
                </div>
              </div>

              <button
                type="button"
                @click="doExport"
                :disabled="exporting || !projectReady"
                class="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-zinc-950 text-[10px] font-bold text-white transition hover:bg-zinc-800 disabled:opacity-40 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100"
              >
                <span class="material-symbols-rounded text-[17px]">download</span>
                {{ exporting ? 'Exporting…' : `Export ${exportSize === 8192 ? '8K' : '4K'} PNG` }}
              </button>

              <button
                type="button"
                @click="saveAndOpenFolderModal"
                :disabled="!projectReady || isSavingToFolder"
                class="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white text-[10px] font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                <span class="material-symbols-rounded text-[17px]">folder_open</span>
                Save to Library
              </button>

              <div class="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                <div class="flex items-center justify-between gap-3">
                  <span class="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400">Format</span>
                  <span class="text-[10px] font-semibold text-zinc-700 dark:text-zinc-200">PNG</span>
                </div>
                <div class="mt-2 flex items-center justify-between gap-3">
                  <span class="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400">Background</span>
                  <span class="text-[10px] font-semibold text-zinc-700 dark:text-zinc-200">Opaque</span>
                </div>
                <div class="mt-2 flex items-center justify-between gap-3">
                  <span class="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400">Project</span>
                  <span class="max-w-[160px] truncate text-[10px] font-semibold text-zinc-700 dark:text-zinc-200">
                    {{ project?.name || 'Texture' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>

    <div
      v-if="isFolderModalOpen"
      class="fixed inset-0 z-[90] grid place-items-center bg-black/55 p-4 backdrop-blur-sm"
      @click.self="closeFolderModal"
    >
      <div
        class="w-full max-w-lg overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950"
      >
        <div class="flex items-start justify-between gap-4 border-b border-zinc-200 p-5 dark:border-zinc-800">
          <div>
            <p class="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-400">Library</p>
            <h3 class="mt-1 text-base font-semibold text-zinc-900 dark:text-white">Save texture</h3>
            <p class="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
              Choose a folder for the current {{ exportSize === 8192 ? '8K' : '4K' }} render.
            </p>
          </div>
          <button
            type="button"
            @click="closeFolderModal"
            :disabled="isSavingToFolder"
            class="grid h-9 w-9 place-items-center rounded-xl border border-zinc-200 bg-zinc-50 text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
          >
            <span class="material-symbols-rounded text-[17px]">close</span>
          </button>
        </div>

        <div class="p-5">
          <div v-if="isFoldersLoading" class="grid min-h-[160px] place-items-center">
            <div class="text-center">
              <div class="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-zinc-200 border-t-sky-500 dark:border-zinc-700 dark:border-t-sky-400"></div>
              <p class="mt-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-zinc-400">Loading folders</p>
            </div>
          </div>

          <div
            v-else-if="!folders.length"
            class="grid min-h-[160px] place-items-center rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 px-6 text-center dark:border-zinc-800 dark:bg-zinc-900/50"
          >
            <div>
              <span class="material-symbols-rounded text-3xl text-zinc-400">folder_off</span>
              <p class="mt-2 text-sm font-medium text-zinc-700 dark:text-zinc-200">No folders found</p>
            </div>
          </div>

          <div v-else class="max-h-[300px] space-y-2 overflow-y-auto pr-1 custom-scroll">
            <button
              v-for="folder in folders"
              :key="folder.id"
              type="button"
              @click="selectedFolderId = folder.id"
              class="flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition"
              :class="selectedFolderId === folder.id
                ? 'border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-300'
                : 'border-zinc-200 bg-zinc-50 text-zinc-600 hover:bg-white dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300 dark:hover:bg-zinc-900'"
            >
              <span class="grid h-9 w-9 place-items-center rounded-xl bg-white shadow-sm dark:bg-zinc-950">
                <span class="material-symbols-rounded text-[17px]">folder</span>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-[11px] font-semibold">{{ folder.name }}</span>
                <span class="mt-0.5 block text-[9px] text-zinc-400">{{ folder.itemsCount || 0 }} item(s)</span>
              </span>
              <span
                v-if="selectedFolderId === folder.id"
                class="material-symbols-rounded text-[17px] text-sky-500"
              >
                check_circle
              </span>
            </button>
          </div>

          <div class="mt-5 flex justify-end gap-2 border-t border-zinc-200 pt-4 dark:border-zinc-800">
            <button
              type="button"
              @click="closeFolderModal"
              :disabled="isSavingToFolder"
              class="h-9 rounded-xl border border-zinc-200 bg-white px-4 text-[10px] font-semibold text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
            >
              Cancel
            </button>
            <button
              type="button"
              @click="confirmSaveToFolder"
              :disabled="!selectedFolderId || isSavingToFolder"
              class="h-9 min-w-[140px] rounded-xl bg-emerald-500 px-5 text-[10px] font-bold text-zinc-950 disabled:opacity-40"
            >
              {{ isSavingToFolder ? 'Saving…' : 'Save texture' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import authService from '@/service/authService'

const route = useRoute()
const router = useRouter()

const API = 'https://backend.nexgridco.com'
const api = axios.create({ baseURL: API })

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token')
  if (token) cfg.headers.Authorization = `Bearer ${token}`
  return cfg
})

const project = ref(null)
const projectReady = ref(false)
const loadingProject = ref(true)
const savingProject = ref(false)
const saveStatus = ref('idle')
const lastSavedAt = ref(null)

const STYLES = ref([])
const currentStyle = ref('')
const seed = ref(4242)
const isSpinning = ref(false)
const color1 = ref('#ffffff')
const color2 = ref('#777777')
const bg = ref('#000000')
const paramVals = reactive({})

const loadingPresets = ref(true)
const rendering = ref(false)
const remixing = ref(false)
const exporting = ref(false)
const errorMessage = ref('')
const previewUrl = ref('')
const exportSize = ref(4096)

const isFolderModalOpen = ref(false)
const isFoldersLoading = ref(false)
const isSavingToFolder = ref(false)
const folders = ref([])
const selectedFolderId = ref(null)

const inspectorTab = ref('controls')

const PREVIEW_SIZE = 768
const EXPORT_SIZES = [
  { label: '4K PNG', short: '4K', value: 4096 },
  { label: '8K PNG', short: '8K', value: 8192 }
]

const paletteBank = [
  ['#ffffff', '#777777', '#000000'],
  ['#f4f4f5', '#a1a1aa', '#09090b'],
  ['#000000', '#4b5563', '#ffffff'],
  ['#f5f5f4', '#78716c', '#1c1917'],
  ['#ffffff', '#5eead4', '#020617'],
  ['#ffffff', '#818cf8', '#09090b'],
  ['#fdf4ff', '#e879f9', '#0a0a0a'],
  ['#fff7ed', '#fb923c', '#0c0a09']
]

const EMPTY_PRESET = {
  id: '',
  name: 'Texture Studio',
  category: 'Loading',
  description: '',
  engine: '',
  tags: [],
  colors: { foreground: '#ffffff', secondary: '#777777', background: '#000000' },
  defaults: {},
  params: {}
}

const curStyleConfig = computed(() =>
  STYLES.value.find((s) => s.id === currentStyle.value) || STYLES.value[0] || EMPTY_PRESET
)

const colorFields = computed(() => [
  { label: 'A', val: color1.value, set: (v) => { color1.value = v } },
  { label: 'B', val: color2.value, set: (v) => { color2.value = v } },
  { label: 'BG', val: bg.value, set: (v) => { bg.value = v } }
])

const allParams = computed(() => {
  const cfg = curStyleConfig.value
  const out = { ...(cfg.defaults || {}) }

  Object.entries(cfg.params || {}).forEach(([key, def]) => {
    const stored = paramVals[`${currentStyle.value}.${key}`]
    out[key] = stored ?? out[key] ?? def.default
  })

  return out
})

const recipe = computed(() => ({
  style: currentStyle.value,
  seed: seed.value,
  colors: {
    foreground: color1.value,
    secondary: color2.value,
    background: bg.value
  },
  params: { ...allParams.value }
}))

const projectState = computed(() => ({
  ...recipe.value,
  exportSize: Number(exportSize.value)
}))

const saveStatusLabel = computed(() => {
  if (saveStatus.value === 'saving') return 'Saving…'
  if (saveStatus.value === 'saved') return 'Saved'
  if (saveStatus.value === 'unsaved') return 'Unsaved'
  if (saveStatus.value === 'error') return 'Save failed'
  return projectReady.value ? 'Saved' : 'Loading'
})

const saveStatusClass = computed(() => {
  if (saveStatus.value === 'saving') return 'text-sky-700 border-sky-500/20 bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30 dark:bg-sky-500/10'
  if (saveStatus.value === 'saved') return 'text-emerald-700 border-emerald-500/20 bg-emerald-400/10 dark:text-emerald-200 dark:border-emerald-300/20'
  if (saveStatus.value === 'unsaved') return 'text-amber-700 border-amber-500/20 bg-amber-400/10 dark:text-amber-200 dark:border-amber-300/20'
  if (saveStatus.value === 'error') return 'text-red-700 border-red-500/20 bg-red-400/10 dark:text-red-200 dark:border-red-300/20'
  return 'text-zinc-500 dark:text-zinc-400'
})

function presetIcon(preset) {
  const category = String(preset?.category || '').toLowerCase()
  if (category.includes('half')) return '◉'
  if (category.includes('burst')) return '✦'
  if (category.includes('grain')) return '░'
  if (category.includes('particle')) return '⁙'
  if (category.includes('glitch')) return '⌁'
  if (category.includes('print')) return '▓'
  if (category.includes('distort')) return '≋'
  if (category.includes('surface')) return '▧'
  if (category.includes('composite')) return '◆'
  if (category.includes('glass')) return '◇'
  if (category.includes('liquid')) return '≈'
  if (category.includes('holo')) return '✧'
  return '✣'
}

function displayParamValue(key, cfg) {
  const value = paramVals[`${currentStyle.value}.${key}`] ?? allParams.value[key] ?? cfg.default
  if (cfg.type !== 'number') return value
  return cfg.step < 1 ? Number(value).toFixed(Math.min(3, decimalPlaces(cfg.step))) : value
}

function decimalPlaces(value) {
  const str = String(value)
  return str.includes('.') ? str.split('.')[1].length : 0
}

function setParam(key, value) {
  paramVals[`${currentStyle.value}.${key}`] = value
}

function getParam(key, cfg) {
  return paramVals[`${currentStyle.value}.${key}`] ?? allParams.value[key] ?? cfg.default
}

function seedPresetParams(preset, overwrite = false) {
  if (!preset?.id) return

  Object.entries(preset.params || {}).forEach(([key, cfg]) => {
    const stateKey = `${preset.id}.${key}`
    if (!overwrite && paramVals[stateKey] !== undefined) return
    paramVals[stateKey] = preset.defaults?.[key] ?? cfg.default
  })
}

function clearParamState() {
  Object.keys(paramVals).forEach((key) => delete paramVals[key])
}

function applyPresetColors(preset) {
  color1.value = preset?.colors?.foreground || '#ffffff'
  color2.value = preset?.colors?.secondary || '#777777'
  bg.value = preset?.colors?.background || '#000000'
}

function switchStyle(id) {
  if (!projectReady.value) return

  const preset = STYLES.value.find((item) => item.id === id)
  if (!preset) return

  currentStyle.value = preset.id
  seedPresetParams(preset)
  applyPresetColors(preset)
  errorMessage.value = ''
}

function makeSeed() {
  if (window.crypto && window.crypto.getRandomValues) {
    const values = new Uint32Array(1)
    window.crypto.getRandomValues(values)
    return (values[0] % 2147483646) + 1
  }

  return Math.floor(Math.random() * 2147483646) + 1
}

function randomSeed() {
  if (!projectReady.value) return
  isSpinning.value = true
  seed.value = makeSeed()
  window.setTimeout(() => { isSpinning.value = false }, 500)
}

async function randomizeCurrent() {
  if (!projectReady.value || !currentStyle.value || remixing.value) return
  remixing.value = true
  errorMessage.value = ''

  try {
    const { data } = await api.post('/textures/randomize', {
      style: currentStyle.value
    })

    if (data?.seed != null) seed.value = Number(data.seed)

    if (data?.colors) {
      color1.value = data.colors.foreground || color1.value
      color2.value = data.colors.secondary || color2.value
      bg.value = data.colors.background || bg.value
    }

    Object.entries(data?.params || {}).forEach(([key, value]) => {
      paramVals[`${currentStyle.value}.${key}`] = value
    })
  } catch (err) {
    errorMessage.value = getApiError(err, 'Não foi possível remixar a textura.')
  } finally {
    remixing.value = false
  }
}

function applyRandomPalette() {
  if (!projectReady.value) return
  const palette = paletteBank[Math.floor(Math.random() * paletteBank.length)]
  color1.value = palette[0]
  color2.value = palette[1]
  bg.value = palette[2]
}

function buildPayload(width, height = width) {
  return {
    style: currentStyle.value,
    seed: Number(seed.value),
    width,
    height,
    colors: {
      foreground: color1.value,
      secondary: color2.value,
      background: bg.value
    },
    params: { ...allParams.value }
  }
}

let previewTimer = null
let previewController = null
let previewRequestId = 0

function schedulePreview() {
  if (loadingPresets.value || loadingProject.value || !projectReady.value || !currentStyle.value) return
  if (previewTimer) window.clearTimeout(previewTimer)
  previewTimer = window.setTimeout(renderPreview, 180)
}

async function renderPreview() {
  if (!projectReady.value || !currentStyle.value) return

  previewController?.abort()
  const controller = new AbortController()
  previewController = controller
  const requestId = ++previewRequestId

  rendering.value = true
  errorMessage.value = ''

  try {
    const response = await api.post(
      '/textures/preview',
      buildPayload(PREVIEW_SIZE),
      {
        responseType: 'blob',
        signal: controller.signal
      }
    )

    if (controller.signal.aborted || requestId !== previewRequestId) return

    const nextUrl = URL.createObjectURL(response.data)
    const previous = previewUrl.value
    previewUrl.value = nextUrl
    if (previous) URL.revokeObjectURL(previous)
  } catch (err) {
    if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
      errorMessage.value = getApiError(err, 'Não foi possível gerar o preview.')
    }
  } finally {
    if (requestId === previewRequestId) rendering.value = false
  }
}

async function renderTextureFile(size) {
  return api.post(
    '/textures/render',
    {
      ...buildPayload(size),
      output: {
        format: 'png',
        quality: 96,
        transparent: false
      }
    },
    { responseType: 'blob' }
  )
}

function getSafeProjectSlug() {
  return String(project.value?.name || 'texture')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'texture'
}

async function doExport() {
  if (!projectReady.value || !currentStyle.value || exporting.value) return
  exporting.value = true
  errorMessage.value = ''

  try {
    const size = Number(exportSize.value)
    const response = await renderTextureFile(size)

    const contentDisposition = response.headers?.['content-disposition'] || ''
    const serverFilename = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1]
    const filename = serverFilename || `${getSafeProjectSlug()}-${currentStyle.value}-${seed.value}-${size}x${size}.png`

    const url = URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch (err) {
    errorMessage.value = getApiError(err, 'Não foi possível exportar a textura.')
  } finally {
    exporting.value = false
  }
}

async function loadFolders() {
  try {
    isFoldersLoading.value = true
    errorMessage.value = ''

    const { data } = await api.get('/images/groups')
    const groups = Array.isArray(data?.groups)
      ? data.groups
      : Array.isArray(data)
        ? data
        : []

    folders.value = groups.map((group) => ({
      id: group.name,
      name: group.name,
      description: '',
      itemsCount: group.count || 0,
      cover: group.cover || null
    }))
  } catch (err) {
    folders.value = []
    errorMessage.value = getApiError(err, 'Não foi possível carregar suas coleções.')
  } finally {
    isFoldersLoading.value = false
  }
}

async function saveAndOpenFolderModal() {
  if (!projectReady.value || !project.value?.id || isSavingToFolder.value) return

  errorMessage.value = ''
  selectedFolderId.value = null
  isFolderModalOpen.value = true
  await loadFolders()
}

function closeFolderModal() {
  if (isSavingToFolder.value) return
  isFolderModalOpen.value = false
  selectedFolderId.value = null
}

async function confirmSaveToFolder() {
  if (
    !projectReady.value ||
    !project.value?.id ||
    !selectedFolderId.value ||
    isSavingToFolder.value
  ) return

  isSavingToFolder.value = true
  errorMessage.value = ''

  try {
    if (saveStatus.value === 'unsaved' || saveStatus.value === 'error') {
      const saved = await saveProjectState()
      if (!saved) return
    }

    const size = Number(exportSize.value)
    const response = await renderTextureFile(size)
    const filename = `${getSafeProjectSlug()}-${currentStyle.value}-${seed.value}-${size}x${size}.png`

    const formData = new FormData()
    formData.append('files', response.data, filename)

    await api.post(
      `/images/upload?group=${encodeURIComponent(selectedFolderId.value)}`,
      formData
    )

    isFolderModalOpen.value = false
    selectedFolderId.value = null
    window.dispatchEvent(new CustomEvent('df:gallery-refresh'))
  } catch (err) {
    errorMessage.value = getApiError(err, 'Não foi possível salvar a textura na coleção.')
  } finally {
    isSavingToFolder.value = false
  }
}

async function loadPresets() {
  loadingPresets.value = true
  errorMessage.value = ''

  try {
    const { data } = await api.get('/textures/presets')
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.presets)
        ? data.presets
        : []

    STYLES.value = list

    if (!list.length) {
      throw new Error('O backend não retornou presets de textura.')
    }

    const preferred = list.find((item) => item.id === 'halftone-nova') || list[0]
    currentStyle.value = preferred.id
    seedPresetParams(preferred, true)
    applyPresetColors(preferred)
  } catch (err) {
    errorMessage.value = getApiError(err, 'Não foi possível carregar os presets do backend.')
    throw err
  } finally {
    loadingPresets.value = false
  }
}

async function loadProject() {
  const projectId = String(route.params.projectId || '')
  if (!projectId) {
    router.replace({ name: 'texture' })
    return
  }

  loadingProject.value = true
  projectReady.value = false
  saveStatus.value = 'idle'

  try {
    const { data } = await api.get(`/textures/projects/${projectId}`)
    project.value = data

    const savedRecipe = data?.recipe || {}
    const savedStyle = data?.style || savedRecipe.style
    const preset = STYLES.value.find((item) => item.id === savedStyle) || STYLES.value[0]

    if (!preset) throw new Error('No texture preset is available for this project.')

    clearParamState()
    currentStyle.value = preset.id
    seedPresetParams(preset, true)

    const savedColors = data?.colors || savedRecipe.colors || {}
    color1.value = savedColors.foreground || preset.colors?.foreground || '#ffffff'
    color2.value = savedColors.secondary || preset.colors?.secondary || '#777777'
    bg.value = savedColors.background || preset.colors?.background || '#000000'

    const savedSeed = data?.seed ?? savedRecipe.seed
    seed.value = Number.isFinite(Number(savedSeed)) ? Number(savedSeed) : 4242

    const savedParams = data?.params || savedRecipe.params || {}
    Object.entries(savedParams).forEach(([key, value]) => {
      paramVals[`${preset.id}.${key}`] = value
    })

    exportSize.value = [4096, 8192].includes(Number(data?.exportSize))
      ? Number(data.exportSize)
      : 4096

    projectReady.value = true
    lastSavedSnapshot = JSON.stringify(projectState.value)
    saveStatus.value = 'saved'
    lastSavedAt.value = data?.updatedAt || new Date().toISOString()
  } catch (err) {
    errorMessage.value = getApiError(err, 'Não foi possível carregar o projeto de textura.')
    saveStatus.value = 'error'
  } finally {
    loadingProject.value = false
  }
}

let lastSavedSnapshot = ''

async function saveProjectState() {
  if (!projectReady.value || !project.value?.id || savingProject.value) return false

  const snapshot = JSON.stringify(projectState.value)

  if (snapshot === lastSavedSnapshot) {
    saveStatus.value = 'saved'
    return true
  }

  const payload = JSON.parse(snapshot)
  savingProject.value = true
  saveStatus.value = 'saving'
  errorMessage.value = ''

  try {
    const { data } = await api.patch(`/textures/projects/${project.value.id}/state`, payload)

    if (data) {
      project.value = {
        ...project.value,
        ...data
      }
    }

    lastSavedSnapshot = snapshot
    lastSavedAt.value = data?.updatedAt || new Date().toISOString()
    saveStatus.value = JSON.stringify(projectState.value) === lastSavedSnapshot ? 'saved' : 'unsaved'
    return true
  } catch (err) {
    saveStatus.value = 'error'
    errorMessage.value = getApiError(err, 'Não foi possível salvar o projeto.')
    return false
  } finally {
    savingProject.value = false
  }
}

function goBackToProjects() {
  router.push({ name: 'texture' })
}

function getApiError(err, fallback) {
  const status = err?.response?.status
  if (status === 401) return 'Sua sessão expirou ou o token não é válido. Faça login novamente.'
  if (status === 403) return 'Sua conta não tem permissão para usar este recurso.'
  if (status === 404) return 'Projeto de textura não encontrado.'

  const message = err?.response?.data?.message
  if (Array.isArray(message)) return message.join(' · ')
  if (typeof message === 'string') return message
  if (typeof err?.message === 'string' && !err.message.includes('status code')) return err.message
  return fallback
}

watch(recipe, schedulePreview, { deep: true })

watch(
  projectState,
  () => {
    if (!projectReady.value || loadingProject.value) return
    saveStatus.value = JSON.stringify(projectState.value) === lastSavedSnapshot ? 'saved' : 'unsaved'
  },
  { deep: true }
)

onMounted(async () => {
  const current = authService.current()
  if (!current) {
    router.replace('/')
    return
  }

  try {
    await loadPresets()
    await loadProject()
    schedulePreview()
  } catch (err) {
    console.error('Texture Studio initialization failed:', err)
  }
})

onUnmounted(() => {
  if (previewTimer) window.clearTimeout(previewTimer)
  previewController?.abort()
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
})
</script>

<style scoped>
.custom-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(113, 113, 122, 0.22);
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.custom-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(113, 113, 122, 0.4);
  border: 2px solid transparent;
  background-clip: content-box;
}

:global(.dark) .custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.13);
  border: 2px solid transparent;
  background-clip: content-box;
}

input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 999px;
  background: rgba(113, 113, 122, 0.18);
  outline: none;
}

input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 15px;
  height: 15px;
  border-radius: 999px;
  background: #0ea5e9;
  cursor: grab;
  border: 3px solid #fff;
  box-shadow: 0 0 0 1px rgba(14, 165, 233, 0.22);
}

:global(.dark) input[type='range']::-webkit-slider-thumb {
  background: #38bdf8;
  border-color: #18181b;
}

input[type='range']::-moz-range-thumb {
  width: 15px;
  height: 15px;
  border-radius: 999px;
  background: #0ea5e9;
  cursor: grab;
  border: 3px solid #fff;
  box-shadow: 0 0 0 1px rgba(14, 165, 233, 0.22);
}

:global(.dark) input[type='range']::-moz-range-thumb {
  background: #38bdf8;
  border-color: #18181b;
}

select {
  -webkit-appearance: none;
  appearance: none;
}
</style>
